import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useBandStore } from "../../stores/bandStore.js";
import { useSongStore } from "../../stores/songStore.js";
import { Button } from "../../ui/Button.js";
import { Badge } from "../../ui/Badge.js";
import { BandChemistry } from "./BandChemistry.js";
import { BandCostBadge } from "./BandCostBadge.js";
import { DeleteBandModal } from "./DeleteBandModal.js";
import { Discography } from "./Discography.js";
import { PersonaCard } from "./PersonaCard.js";
import { SonicTraitsCard } from "./SonicTraitsCard.js";
import { Stage } from "./Stage.js";
import { StageEditor } from "./StageEditor.js";
import { usePlayingBands } from "./usePlayingBands.js";

export function BandDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const bands = useBandStore((s) => s.bands);
  const songs = useSongStore((s) => s.songs);
  const band = bands.find((b) => b.id === id);
  const [creating, setCreating] = useState(false);
  const [stageEditorOpen, setStageEditorOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const playingBands = usePlayingBands();
  const isPlaying = band ? playingBands.has(band.id) : false;

  useEffect(() => {
    if (!id) return;
    void useBandStore.getState().fetch();
    void useSongStore.getState().fetchByBand(id);
  }, [id]);

  if (!band)
    return (
      <p className="font-mono text-xs uppercase tracking-widest2 text-ink-muted">
        Banda não encontrada.
      </p>
    );

  async function onNewSong(): Promise<void> {
    if (!band) return;
    setCreating(true);
    try {
      const song = await useSongStore.getState().create({ bandId: band.id });
      nav(`/songs/${song.project_id}`);
    } finally {
      setCreating(false);
    }
  }

  async function onConfirmDelete(): Promise<void> {
    if (!band) return;
    await useBandStore.getState().remove(band.id);
    setDeleteOpen(false);
    nav("/bands");
  }

  return (
    <div className="flex flex-col gap-10">
      {/* Header / catalog ficha */}
      <header className="reveal flex flex-col gap-4 border-b border-border pb-6">
        <div className="flex items-center justify-between">
          <Link
            to="/bands"
            className="font-mono text-[10px] uppercase tracking-widest2 text-ink-muted hover:text-paper"
          >
            ← catálogo
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <BandCostBadge bandId={band.id} />
            <Link to={`/bands/${band.id}/edit`}>
              <Button variant="secondary">Editar elenco</Button>
            </Link>
            <Button variant="danger" onClick={() => setDeleteOpen(true)}>
              Apagar
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
          <h1
            className="font-display text-[44px] leading-[0.9] tracking-tight text-paper sm:text-[64px] md:text-[92px] md:leading-[0.85]"
            style={{ fontVariationSettings: '"opsz" 144, "wght" 600, "SOFT" 100, "WONK" 1' }}
          >
            {band.name}
          </h1>
          <dl className="grid grid-cols-3 gap-x-6 font-mono text-[10px] uppercase tracking-widest2">
            <dt className="text-ink-dim">faixas</dt>
            <dt className="text-ink-dim">elenco</dt>
            <dt className="text-ink-dim">formada</dt>
            <dd className="text-paper tabular-nums">{String(band.song_count).padStart(2, "0")}</dd>
            <dd className="text-paper tabular-nums">
              {String(band.slots.length).padStart(2, "0")} / 05
            </dd>
            <dd className="text-paper tabular-nums">
              {new Date(band.created_at).toLocaleDateString("pt-BR")}
            </dd>
          </dl>
        </div>
      </header>

      {/* Stage */}
      <section className="reveal-1 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="catalog-num">PALCO · {isPlaying ? "AO VIVO" : "ENSAIO"}</p>
          <Button variant="ghost" onClick={() => setStageEditorOpen((o) => !o)}>
            {stageEditorOpen ? "fechar editor" : "customizar palco"}
          </Button>
        </div>
        <Stage band={band} playing={isPlaying} />
        {stageEditorOpen && <StageEditor band={band} onClose={() => setStageEditorOpen(false)} />}
      </section>

      {/* Som da banda — read-only aggregated traits derived from the slot artists */}
      <section className="reveal-1 flex flex-col gap-3">
        <p className="catalog-num">SOM DA BANDA · DERIVADO</p>
        <div className="border border-border bg-tape p-5 shadow-sleeve">
          <p className="mb-4 font-mono text-[10px] leading-relaxed text-ink-dim">
            Traços combinados dos artistas dos slots, ponderados pela mixagem de som. Para mudar,
            edite a{" "}
            <Link
              to={`/bands/${band.id}/edit`}
              className="text-butter underline-offset-2 hover:underline"
            >
              identidade sonora de cada artista
            </Link>{" "}
            ou ajuste os pesos em <span className="text-paper">customizar palco</span>.
          </p>
          <SonicTraitsCard
            traits={band.aggregated_traits}
            emptyHint="Nenhum artista dos slots tem identidade sonora declarada — a geração desta banda fica neutra."
          />
        </div>
      </section>

      {/* Persona da banda — read-only aggregated persona derived from the slot artists */}
      <section className="reveal-1 flex flex-col gap-3">
        <p className="catalog-num">PERSONA DA BANDA · DERIVADO</p>
        <div className="border border-border bg-tape p-5 shadow-sleeve">
          <p className="mb-4 font-mono text-[10px] leading-relaxed text-ink-dim">
            Temperamento e influências combinados dos artistas dos slots, ponderados pela mixagem de
            som. Para mudar, edite a{" "}
            <Link
              to={`/bands/${band.id}/edit`}
              className="text-butter underline-offset-2 hover:underline"
            >
              persona de cada artista
            </Link>{" "}
            no elenco.
          </p>
          <PersonaCard
            persona={band.aggregated_persona}
            emptyHint="Nenhum artista dos slots tem persona declarada — a banda não tem temperamento nem influências."
          />
        </div>
      </section>

      {/* Química da banda — the free A/B preview */}
      <section className="reveal-2 flex flex-col gap-3">
        <p className="catalog-num">QUÍMICA DA BANDA · A/B</p>
        <div className="border border-border bg-tape p-5 shadow-sleeve">
          <BandChemistry band={band} />
        </div>
      </section>

      {/* Faixas pool */}
      <section className="reveal-2 flex flex-col gap-4">
        <div className="flex items-end justify-between border-b border-border pb-3">
          <div>
            <p className="catalog-num">FAIXAS · POOL</p>
            <h2
              className="font-display text-3xl text-paper"
              style={{ fontVariationSettings: '"opsz" 60, "wght" 600, "SOFT" 80' }}
            >
              {songs.length === 0
                ? "Nenhuma faixa ainda"
                : `${songs.length} faixa${songs.length === 1 ? "" : "s"}`}
            </h2>
          </div>
          <Button variant="primary" onClick={() => void onNewSong()} disabled={creating}>
            + Nova faixa
          </Button>
        </div>

        {songs.length === 0 && (
          <div className="flex flex-col items-center gap-3 border border-dashed border-border p-8 text-center">
            <p className="font-display text-xl text-paper">Sem músicas ainda</p>
            <p className="font-mono text-xs uppercase tracking-widest2 text-ink-muted">
              clique em <span className="text-paper">nova faixa</span> pra entrar no estúdio
            </p>
            {band.slots.length === 0 && (
              <Link
                to={`/bands/${band.id}/edit`}
                className="mt-2 font-mono text-[10px] uppercase tracking-widest2 text-butter underline-offset-2 hover:underline"
              >
                preencher elenco primeiro →
              </Link>
            )}
          </div>
        )}

        <ol className="flex flex-col">
          {songs.map((s, i) => (
            <Link
              key={s.project_id}
              to={`/songs/${s.project_id}`}
              className="group grid grid-cols-12 items-center gap-3 border-b border-border py-3 transition-colors hover:bg-tape"
            >
              <span className="col-span-1 font-mono text-xs tabular-nums text-ink-dim">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="col-span-7">
                <div
                  className="font-display text-xl leading-tight text-paper group-hover:text-butter"
                  style={{ fontVariationSettings: '"opsz" 36, "wght" 500, "SOFT" 60' }}
                >
                  {s.title ?? <em className="text-ink-muted">sem título</em>}
                </div>
                <div className="catalog-num mt-1 truncate">{s.project_id}</div>
              </div>
              <div className="col-span-3 text-right">
                <Badge
                  tone={
                    s.title_status === "done" || s.title_status === "manual" ? "active" : "neutral"
                  }
                >
                  {s.title_status === "pending" ? "gerando título…" : s.title_status}
                </Badge>
              </div>
              <span className="col-span-1 text-right font-mono text-[10px] uppercase tracking-widest2 text-ink-muted group-hover:text-paper">
                abrir →
              </span>
            </Link>
          ))}
        </ol>
      </section>

      {/* Discografia — chronological trajectory + style-evolution summary */}
      <section className="reveal-2 flex flex-col gap-3">
        <p className="catalog-num">DISCOGRAFIA · LINHA DO TEMPO</p>
        <p className="font-mono text-[10px] leading-relaxed text-ink-dim">
          A trajetória cronológica de faixas da banda, com a leitura de como o estilo evoluiu.
        </p>
        <Discography subject={{ kind: "band", id: band.id }} />
      </section>

      <DeleteBandModal
        bandId={band.id}
        bandName={band.name}
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => void onConfirmDelete()}
      />
    </div>
  );
}
